import {Intervals} from "../../Intervals"

/**
 * MKT (Mean Kinetic Temperature) Report settings schema.
 *
 * The report variation (`reportType`) is an `optioncards` field: each card
 * describes what that report type produces. It is a manual-run setting, so
 * this section renders in the Report Settings drawer on an on-demand run as
 * well as on a template.
 * Setting keys (`reportType`) are unchanged from the pre-schema
 * MktReportSettings.vue.
 */

export const MKT_CALCULATION = 'NABP_REPORT_MKT_CALCULATION'
export const MKT_DETAILED = 'NABP_REPORT_MKT_DETAILED'

export default {
    slug: 'mkt-report',
    icon:
        '<path stroke="none" d="M0 0h24v24H0z" fill="none"/>'
        + '<path d="M7 20l10 0" />'
        + '<path d="M6 6l6 -1l6 1" />'
        + '<path d="M12 3l0 17" />'
        + '<path d="M9 12l-3 -6l-3 6a3 3 0 0 0 6 0" />'
        + '<path d="M21 12l-3 -6l-3 6a3 3 0 0 0 6 0" />',
    permission: 'manage_excursion_report',
    title: 'Mean Kinetic Temperature',
    description: 'How much heat exposure each point saw over the period, against its limits.',
    info: 'Mean kinetic temperature (MKT) is one temperature that reflects the total heat stress on stored product, weighting warm periods more heavily. It is calculated from every reading on each temperature point in the period.',

    sections: [
        {
            title: 'Format',
            description: 'Choose which MKT report to generate.',
            // Under the Points card, in the wide column, as in the design.
            cardColumn: 2,
            fields: [
                {
                    // Selectable cards: the two options describe themselves and the
                    // card IS the control, so there is no separate dropdown.
                    key: 'reportType',
                    type: 'optioncards',
                    options: {
                        [MKT_CALCULATION]: {
                            label: 'MKT Calculation',
                            cardLabel: 'Summary: one row per point',
                            description: 'One row per point: area, zone, point name, MKT for the period, and the point\'s current low and high alarm limits.',
                        },
                        [MKT_DETAILED]: {
                            label: 'MKT Detailed',
                            cardLabel: 'Detailed and Graph: daily highs and lows with a graph for each point',
                            description: 'A page per point for each month: daily minimum and maximum, the period\'s MKT, and minutes out of range each day, with a graph showing alarm and caution limits.',
                        },
                    },
                },
            ],
        },

        {
            // Template-only: the recurring send schedule. On-demand runs pick an
            // explicit range on the run form, so this is hidden there.
            title: 'Scheduled Delivery',
            description: 'Automatically send this report on a recurring schedule.',
            showIf: ({isSavable}) => isSavable === true,
            fields: [
                {
                    key: 'interval',
                    type: 'select',
                    label: 'Send frequency',
                    description: 'How often the report is emailed, around 1 AM site time. Daily covers the previous day, weekly the 7 days before, semi-monthly each half month, and monthly the previous month.',
                    options: Intervals.INTERVAL_OPTIONS,
                    fromContext: true,
                },
                {
                    key: 'weekday',
                    type: 'select',
                    label: 'Day to receive report',
                    description: 'The day the weekly report is emailed, around 1 AM site time. It covers the 7 days ending the day before.',
                    options: Intervals.WEEKDAY_OPTIONS,
                    showIf: ({interval}) => interval === Intervals.WEEKLY,
                },
            ],
        },
    ],
}
